// ============================================================================
// src/lib/userDirectoryApi.ts — the one physical full-profile-directory read
// ============================================================================
// Extracted VERBATIM from src/shared/userDirectoryData.tsx. Nothing about the
// request changed: same chain, same order, same abort plumbing, same error
// contract. What changed is only where it lives — a `.tsx` provider no longer
// owns a PostgREST chain (ask 3), and the transport is now importable and
// testable without a renderer.
//
// WHAT THIS IS NOT, AND MUST NOT BECOME
//
// It is NOT a routeDataCatalog resource, and the difference is not a filing
// question. `profiles` has ZERO readers in the read catalog, deliberately: this
// directory is CLICK-TRIGGERED (an admin opens Users, or enters the Webforms
// editor), not route-scoped, and it is already TanStack-backed through
// appQueryClient.fetchQuery on `userDirectoryQueryKey`. Declaring it as a route
// resource would attach it to a surface's load, which is exactly the
// "requested on demand, never at provider mount or application boot" contract
// the provider's own header protects.
//
// THE THREE PROPERTIES A CALLER DEPENDS ON, ALL PRESERVED
//
//   * ERRORS THROW, with the `user directory: <message>` prefix. The provider's
//     catch is what keeps last-good same-session rows on screen instead of
//     publishing a fabricated empty roster, and it can only do that if a
//     failure arrives as a rejection.
//   * `[]` IS DATA, NOT AN ERROR. An empty directory is a legitimate answer and
//     resolves normally.
//   * THE ABORT SIGNAL REACHES THE CHAIN. Query hands `fetchQuery` a signal so
//     it can supersede an in-flight request; `withSignal` applies it through
//     PostgREST's own `.abortSignal()` when the builder offers one, and is a
//     no-op when it does not (a fake client in a test, for instance).
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';

type AbortableRequest = {
  then: unknown;
  abortSignal?: (signal: AbortSignal) => AbortableRequest;
};

function withSignal<Request extends AbortableRequest>(request: Request, signal?: AbortSignal): Request {
  return signal && typeof request.abortSignal === 'function' ? (request.abortSignal(signal) as Request) : request;
}

/** The one physical full-directory read. Errors stay errors; [] is data. */
export async function readUserDirectory(client: SupabaseClient, signal?: AbortSignal): Promise<unknown[]> {
  const {data, error} = await withSignal(client.from('profiles').select('*').order('created_at'), signal);
  if (error) throw new Error(`user directory: ${error.message || error}`);
  return data || [];
}
